import React, { useEffect } from 'react';
import { Form, Input, Button, Typography, App } from 'antd';
import {
  LockOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  EyeInvisibleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { resetPassword } from '../../store/slices/authSlice';
import { AuthSplitLayout } from './AuthSplitLayout';
import { AuthFormCard } from './AuthFormCard';
import {
  AuthHeroBadge,
  AuthHeroTitle,
  AuthHeroDescription,
  AuthHeroHighlights,
  AuthHeroFeatureHeading,
  AuthHeroFeatureList,
} from './authHeroPrimitives';

const { Text } = Typography;

const inputClass =
  '!min-h-[2.75rem] rounded-lg border-slate-200 text-[15px] transition-colors hover:border-[#0171BF]/40 focus:border-[#0171BF]';

const ResetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) navigate('/forgot-password');
  }, [token, navigate]);

  const onFinish = async (values: { password: string; confirmPassword: string }) => {
    if (!token) return;
    try {
      await dispatch(resetPassword({ token, password: values.password })).unwrap();
      message.success('Password updated! You can sign in with your new password.');
      navigate('/login');
    } catch (err) {
      message.error(err as string);
    }
  };

  if (!token) return null;

  const highlights = [
    { value: 'Strong secret', label: 'New password' },
    { value: 'Old link', label: 'Stops working' },
    { value: 'All devices', label: 'Use new sign-in' },
  ];

  const features = [
    {
      icon: <KeyOutlined />,
      title: 'Link is single-use',
      description: 'After success, request another reset if you still need access—tokens do not last forever.',
    },
    {
      icon: <EyeInvisibleOutlined />,
      title: 'Pick something unique',
      description: 'Avoid passwords you use on other sites—this protects your vendor inbox and listings.',
    },
    {
      icon: <ThunderboltOutlined />,
      title: 'Back to work fast',
      description: 'Once saved, return to sign in and continue managing leads and catalogue updates.',
    },
  ];

  const hero = (
    <>
      <AuthHeroBadge icon={<LockOutlined />}>New credentials</AuthHeroBadge>
      <AuthHeroTitle>Choose a new password you will remember</AuthHeroTitle>
      <AuthHeroDescription>
        You opened a valid reset link from your email. Set a strong password now—when you are done, sign in on the
        vendor portal as usual. Your listings and lead history stay tied to the same account.
      </AuthHeroDescription>
      <AuthHeroHighlights items={highlights} />
      <AuthHeroFeatureHeading>Good to know</AuthHeroFeatureHeading>
      <AuthHeroFeatureList items={features} />
    </>
  );

  const leftFooter = (
    <>
      <span className="inline-flex items-center gap-1">
        <CheckCircleOutlined className="text-[#4ADE80]" />
        Encrypted password update
      </span>
      <span className="text-white/50">·</span>
      <span className="inline-flex items-center gap-1 text-white/70">
        <SafetyCertificateOutlined className="text-xs text-[#F7941F]" />
        Same account, new sign-in secret
      </span>
    </>
  );

  return (
    <AuthSplitLayout hero={hero} footer={leftFooter}>
      <AuthFormCard
        title="Set new password"
        subtitle="Use at least eight characters with upper and lower case, a number, and a symbol."
      >
        <Form
          form={form}
          name="resetPassword"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
          className="auth-form-fields-tight"
        >
          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Enter a new password' },
              { min: 8, message: 'At least 8 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: 'Upper, lower, number & special char',
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-[#0171BF]" />}
              placeholder="New password (8+ chars, Aa1!)"
              autoComplete="new-password"
              aria-label="New password"
              className={inputClass}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-[#0171BF]" />}
              placeholder="Confirm new password"
              autoComplete="new-password"
              aria-label="Confirm new password"
              className={inputClass}
            />
          </Form.Item>

          {error && (
            <div className="mb-3 rounded-lg border border-red-200/80 bg-red-50/90 px-3.5 py-2.5" role="alert">
              <Text className="text-sm text-red-700">{error}</Text>
            </div>
          )}

          <Form.Item className="auth-form-item--compact">
            <Button type="primary" htmlType="submit" loading={loading} block size="large" className="!h-12 !rounded-lg !font-semibold">
              Update password
            </Button>
          </Form.Item>
        </Form>

        <p className="mb-0 mt-4 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0171BF] hover:text-[#015a9e] hover:underline"
          >
            <ArrowLeftOutlined />
            Back to sign in
          </Link>
        </p>

        <p className="mt-6 text-center text-xs leading-relaxed text-slate-500 sm:text-[13px]">
          By saving a new password you confirm this change is authorised by you and accept our latest{' '}
          <Link to="/terms" className="font-medium text-[#0171BF] hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="font-medium text-[#0171BF] hover:underline">
            Privacy Policy
          </Link>{' '}
          for continued vendor access.
        </p>
      </AuthFormCard>
    </AuthSplitLayout>
  );
};

export default ResetPassword;
